/**
 * Schemas Zod do convite de membros da clinic (PR-M2-03).
 *
 * Espelha o whitelist de roles aceitos pelo `invite-clinic-member`
 * edge function — qualquer mudança lá precisa ser refletida aqui
 * (decisão consciente: o frontend valida ANTES de bater na função).
 */
import { z } from 'zod';

/** Roles convidáveis pela UI `/equipe` — exclui owner (único, criado
 * no provision-clinic), superadmin (global) e patient (módulo futuro).
 *
 * Mantém ordem alinhada com `roles.sort_order` do banco (admin, médico,
 * enfermagem, recepção, financeiro). */
export const INVITABLE_ROLE_KEYS = [
  'clinic_admin',
  'medico',
  'enfermagem',
  'recepcao',
  'financeiro',
] as const;

export type InvitableRoleKey = (typeof INVITABLE_ROLE_KEYS)[number];

export const inviteClinicMemberSchema = z.object({
  // Preprocess trim+lowercase ANTES da validação de formato. Sem isso,
  // input "  MEDICO@X.COM " falha em `.email()` por causa dos espaços
  // — UX ruim: user nem sabe que tinha espaço extra.
  email: z.preprocess(
    (v) => (typeof v === 'string' ? v.trim().toLowerCase() : v),
    z
      .string()
      .min(1, 'clinics.team.invite.validation.email.required')
      .email('clinics.team.invite.validation.email.invalid'),
  ),
  full_name: z.preprocess(
    (v) => (typeof v === 'string' ? v.trim() : v),
    z
      .string()
      .min(2, 'clinics.team.invite.validation.fullName.tooShort')
      .max(120, 'clinics.team.invite.validation.fullName.tooLong'),
  ),
  role_key: z.enum(INVITABLE_ROLE_KEYS, {
    errorMap: () => ({ message: 'clinics.team.invite.validation.roleKey.invalid' }),
  }),
});

export type InviteClinicMemberInput = z.infer<typeof inviteClinicMemberSchema>;
